/**
 * 収集カレンダー設定サービス
 * モック/本番API切り替え対応
 * 作業方針 8.6 収集カレンダー設定の仕様に準拠
 */

import { USE_MOCK_API } from '../config.js'

// モックデータ
let mockCalendarData = {
  '2024-04': {
    '1': {  // 地区ID: 1
      '2024-04-01': [
        { id: 5, typeName: 'プラスチックごみ', category: 'waste' }
      ],
      '2024-04-02': [
        { id: 1, typeName: '燃えるごみ', category: 'waste' }
      ],
      '2024-04-04': [
        { id: 1, typeName: '燃えるごみ', category: 'waste' },
        { id: 3, typeName: '資源ごみ', category: 'waste' }
      ],
      '2024-04-09': [
        { id: 1, typeName: '燃えるごみ', category: 'waste' }
      ],
      '2024-04-10': [
        { id: 5, typeName: 'プラスチックごみ', category: 'waste' }
      ],
      '2024-04-11': [
        { id: 1, typeName: '燃えるごみ', category: 'waste' },
        { id: 3, typeName: '資源ごみ', category: 'waste' }
      ]
    }
  }
}

// モック用ユーティリティ
function simulateDelay(ms = 500) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// =============================================================================
// モックAPI実装
// =============================================================================

const mockApi = {
  /**
   * カレンダーデータ取得
   * @param {number} year - 対象年
   * @param {number} month - 対象月（1-12）
   * @param {number} districtId - 収集地区ID
   * @returns {Promise<Object>} カレンダーデータ
   */
  async getCalendarData(year, month, districtId) {
    await simulateDelay(500)

    const key = `${year}-${String(month).padStart(2, '0')}`
    const districtData = mockCalendarData[key]?.[districtId] || {}

    console.log(`[Mock API] カレンダーデータ取得: ${year}年${month}月 地区ID:${districtId}`)

    return {
      year,
      month,
      districtId,
      events: districtData
    }
  },

  /**
   * 収集スケジュール作成・更新
   * @param {string} date - 収集日（YYYY-MM-DD）
   * @param {number} districtId - 収集地区ID
   * @param {number[]} wasteTypeIds - ごみ種別IDの配列
   * @returns {Promise<Object>} 更新結果
   */
  async updateCalendarDay(date, districtId, wasteTypeIds) {
    await simulateDelay(300)

    const [year, month] = date.split('-')
    const key = `${year}-${month}`

    if (!mockCalendarData[key]) {
      mockCalendarData[key] = {}
    }
    if (!mockCalendarData[key][districtId]) {
      mockCalendarData[key][districtId] = {}
    }

    // ごみ種別IDを名前に変換（wasteTypeServiceのモックデータと統一）
    const wasteTypeNames = {
      1: '燃えるごみ',
      2: '燃えないごみ',
      3: '資源ごみ',
      4: '粗大ごみ',
      5: 'プラスチックごみ',
      6: '有害ごみ'
    }

    if (wasteTypeIds.length === 0) {
      // 空配列の場合は削除
      delete mockCalendarData[key][districtId][date]
    } else {
      mockCalendarData[key][districtId][date] = wasteTypeIds.map(id => ({
        id,
        typeName: wasteTypeNames[id] || `種別${id}`,
        category: 'waste'
      }))
    }

    console.log(`[Mock API] カレンダー更新: ${date} 地区ID:${districtId}`, wasteTypeIds)

    return {
      success: true,
      data: {
        date,
        districtId,
        wasteTypes: mockCalendarData[key][districtId][date] || []
      }
    }
  },

  /**
   * 収集地区一覧取得（collectionDistrictServiceから取得予定）
   */
  async getDistricts() {
    await simulateDelay(300)

    return [
      { id: 1, districtName: '第1収集地区' },
      { id: 2, districtName: '第2収集地区' },
      { id: 3, districtName: '第3収集地区' }
    ]
  }
}

// =============================================================================
// 本番API実装
// =============================================================================

const productionApi = {
  async getCalendarData(year, month, districtId) {
    try {
      const response = await fetch(
        `/api/system-settings/collection-calendars?year=${year}&month=${month}&district=${districtId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('カレンダーデータの取得に失敗しました:', error)
      throw new Error('カレンダーデータの取得に失敗しました')
    }
  },

  async updateCalendarDay(date, districtId, wasteTypeIds) {
    try {
      const response = await fetch('/api/system-settings/collection-calendars', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date,
          districtId,
          wasteTypeIds
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('カレンダーの更新に失敗しました:', error)
      throw new Error('カレンダーの更新に失敗しました')
    }
  },

  async getDistricts() {
    // 本番環境ではcollectionDistrictServiceを使用
    const response = await fetch('/api/system-settings/collection-districts')
    return await response.json()
  }
}

// =============================================================================
// 統合サービスインターフェース
// =============================================================================

const apiService = USE_MOCK_API ? mockApi : productionApi

if (USE_MOCK_API) {
  console.log('🔧 Using mock API for collection calendar')
} else {
  console.log('🌐 Using production API for collection calendar')
}

/**
 * カレンダーデータ取得
 */
const getCalendarData = async (year, month, districtId) => {
  try {
    return await apiService.getCalendarData(year, month, districtId)
  } catch (error) {
    console.error('カレンダーデータの取得エラー:', error)
    throw error
  }
}

/**
 * 収集スケジュール更新
 */
const updateCalendarDay = async (date, districtId, wasteTypeIds) => {
  try {
    return await apiService.updateCalendarDay(date, districtId, wasteTypeIds)
  } catch (error) {
    console.error('カレンダー更新エラー:', error)
    throw error
  }
}

/**
 * 現在使用中のAPIタイプを取得
 */
const getApiType = () => {
  return USE_MOCK_API ? 'mock' : 'production'
}

export default {
  getCalendarData,
  updateCalendarDay,
  getApiType
}
